(ns app.api-credential.create-modal
  "Modal for creating a new API credential."
  (:require [storrito.ui.core :as ui]
            [parts.replicant.command :as command]
            [ui.action-handlers :as action-handlers]
            [app.api-credential.page :as page]
            [app.api-credential.usage :as usage]))

(def create-command
  {:command/kind :command/api-credential-create})

(defn copy-to-clipboard
  "Copies `text` and shows `Copied!` on `btn` for a moment. Uses the
   shared helper that never rejects: `writeText` rejects with a
   `NotAllowedError` when the document is not focused (Sentry:
   STORRITO-V2-M9), which was an unhandled rejection here."
  [text btn]
  (-> (action-handlers/copy-to-clipboard! text)
      (.then (fn [copied]
               (when copied
                 (let [original-text (.-textContent btn)]
                   (set! (.-textContent btn) "Copied!")
                   (js/setTimeout
                     (fn [] (set! (.-textContent btn) original-text))
                     1500)))))))

(def ^:private copy-btn-class
  ["ml-2" "inline-flex" "items-center" "px-2" "py-1" "text-xs"
   "font-medium" "text-gray-600" "bg-gray-100" "rounded"
   "hover:bg-gray-200" "cursor-pointer"])

(defn field-header
  "The label of a copyable value and its Copy button. `id` is the one of
   the labelled control."
  [{:keys [id label value]}]
  [:div.flex.items-center.justify-between
   [:label {:for id
            :class "block text-sm font-medium leading-6 text-gray-900"}
    label]
   [:button
    {:type "button"
     :class copy-btn-class
     :on {:click (fn [e]
                   (copy-to-clipboard value (.-target e)))}}
    "Copy"]])

(defn field-with-copy
  [{:keys [id value] :as field}]
  [:div.mt-4
   (field-header field)
   [:div.mt-1
    [:input
     {:id id
      :type "text"
      :readOnly true
      :class "block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50 sm:text-sm sm:leading-6"
      :value value}]]])

(defn snippet-with-copy
  "Like `field-with-copy`, for a value with several lines: a read-only
   textarea, so the label can refer to it and the lines do not wrap."
  [{:keys [id value rows] :as field}]
  [:div.mt-4
   (field-header field)
   [:div.mt-1
    [:textarea
     {:id id
      :rows rows
      :wrap "off"
      :readOnly true
      :spellCheck "false"
      :class "block w-full resize-none whitespace-pre overflow-x-auto rounded-md border-0 py-1.5 font-mono text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 bg-gray-50"
      :value value}]]])

(defn created
  "The modal after the creation: the token, the base URL of this
   organization's API and a first request, each with a Copy button. The
   origin comes from the modal params, see `modal`."
  [state]
  (let [result (command/get-latest-result state create-command)
        token (usage/token result)
        origin (get-in state [:ui/modal :params :origin])]
    [:div.py-4
     [:div.text-center
      [:h3 {:class "text-base font-semibold leading-6 text-gray-900"}
       "New API Credential was created"]
      [:p {:class "mt-2 text-sm text-gray-500"}
       "The secret is shown only once. Afterwards it will not be possible to retrieve it again. Treat it like a password."]]
     [:div.mt-4
      (field-with-copy {:id "api-credential-token"
                        :label "Bearer Token"
                        :value token})
      (field-with-copy {:id "api-credential-base-url"
                        :label "API base URL"
                        :value (usage/base-url origin)})
      [:p {:class "mt-3 text-sm text-gray-500"}
       "The subdomain of the URL is your organization UUID, not the credential id. The token always goes in the "
       [:code "Authorization: Bearer <your-token>"]
       " header, never in the URL."]
      (snippet-with-copy {:id "api-credential-example"
                          :label "Example request (bash)"
                          :rows 4
                          :value (usage/curl-example {:origin origin
                                                      :token token})})]
     [:div.mt-6
      [:button
       {:class (concat ui/button-primary ["w-full"])
        :on {:click [[:data/query page/api-credentials-query]
                     [:ui/modal-close]]}}
       "Close"]]]))

(defn render
  [state]
  (if (command/success? state create-command)
    (created state)
    [:div.py-4
     [:div.text-center
      [:h3 {:class "text-base font-semibold leading-6 text-gray-900"}
       "New API Credential"]
      [:div.mt-4
       [:div {:class "rounded-md bg-yellow-50 p-4"}
        [:div {:class "flex"}
         [:div {:class "flex-shrink-0"}
          [:svg {:class "h-5 w-5 text-yellow-400"
                 :viewBox "0 0 20 20"
                 :fill "currentColor"
                 :aria-hidden "true"}
           [:path {:fill-rule "evenodd"
                   :d "M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                   :clip-rule "evenodd"}]]]
         [:div {:class "ml-3"}
          [:div {:class "text-sm text-yellow-700"}
           [:p
            "API-Credentials are comparable to your email plus password. Please only hand over them to persons, tools and services "
            [:b "you absolutely trust. "]
            "They will have full access to your Storrito account."]]]]]]]
     [:div.mt-6
      [:button
       {:class (concat ui/button-primary ["w-full"])
        :on {:click [[:data/command
                      create-command
                      {:on-error [[:ui/show-error]]}]]}}
       "Create"]]]))

(def modal
  {:modal-id :modal/api-credential-create
   :on-load (fn [_]
              [[:store/assoc-in
                [:ui.command/log create-command]
                nil]
               ;; The API runs on the host of this page, whose subdomain
               ;; is the organization UUID. Read here, so `render` stays
               ;; pure.
               [:store/assoc-in
                [:ui/modal :params :origin]
                js/window.location.origin]])
   :render #'render})
